import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import { mapShoppingListResponse } from 'src/sections/shopping_lists/shoppingListProductMapper';
import {
  GetShoppingListRequest,
  ShoppingListResponse,
} from 'src/types/faviriteProducts/getShoppingList';
import { ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import axiosInstance from 'src/utils/axios';

export interface ShoppingListQueryResult {
  totalRecords: number;
  items: ShoppingListProduct[];
}

interface UseGetShoppingListQueryOptions {
  request: GetShoppingListRequest;
  enabled?: boolean;
}

export const normalizeShoppingListFolderIds = (folderIds: number[]): number[] =>
  Array.from(
    new Set(folderIds.filter((folderId) => Number.isFinite(folderId) && folderId > 0))
  );

export async function fetchShoppingList(
  request: GetShoppingListRequest
): Promise<ShoppingListResponse> {
  const payload: GetShoppingListRequest = {
    ...request,
    folderIds: normalizeShoppingListFolderIds(request.folderIds),
  };
  const response = await axiosInstance.post<ShoppingListResponse>(api.getShoppingList, payload);
  return response.data;
}

export const isStaleShoppingListQuery = (
  queryKey: readonly unknown[],
  activeListId: number
): boolean => {
  const request = queryKey[2];
  if (!request || typeof request !== 'object' || !('folderIds' in request)) {
    return true;
  }

  const folderIds = (request as GetShoppingListRequest).folderIds;
  return !Array.isArray(folderIds) || !folderIds.includes(activeListId);
};

export const removeInactiveShoppingListQueries = (
  queryClient: QueryClient,
  activeListId: number
): void => {
  queryClient.removeQueries({
    queryKey: [...queryKeys.favorite.root, 'shoppingList'],
    predicate: (query) => isStaleShoppingListQuery(query.queryKey, activeListId),
  });
};

export const useGetShoppingListQuery = ({
  request,
  enabled = true,
}: UseGetShoppingListQueryOptions): UseQueryResult<ShoppingListQueryResult> =>
  useQuery<ShoppingListResponse, Error, ShoppingListQueryResult>({
    queryKey: queryKeys.favorite.shoppingList(request),
    queryFn: () => fetchShoppingList(request),
    enabled,
    gcTime: 0,
    select: mapShoppingListResponse,
  });

export default useGetShoppingListQuery;
